import i18n from "@/shared/i18n";
import SaleModel from "@/modules/sales/Sale.model";
import NoteForm from "@/modules/customer/models/NoteForm.model";

import CustomerService from "@/modules/customer/CustomerService";
import ReceiptService from "@/modules/receipt/ReceiptService";
import CategoryService from "@/modules/setup/category/CategoryService";
import InventoryModel from "@/modules/inventory/Inventory.model";
import SettlementModel from "@/modules/settlements/Settlement.model";
import BidModel from "@/modules/pre-bids/models/Bid.model";
import BiddersService from "@/modules/bidders/BiddersService";
import {invoiceLabels, notificationLabels} from "@/modules/invoices/invoiceActivityHelper"
import moment from "moment";
import SalesService from "@/modules/sales/SalesService";
import {Formatter} from "@/core/services/formatter.service";
import HelperService from "@/core/services/helper.service";
import {store} from "@/plugins/vuex";

const saleActivityLabels = {
      lot_no: i18n.t('Lot No'),
      amount: i18n.t('Amount'),
      paddle_number: i18n.t('Paddle Number'),
      type: i18n.t('Type'),
      status: i18n.t('Status'),
      date: i18n.t('Date'),
      bidder: i18n.t('Bidder'),
      auction: i18n.t('Auction'),
      sale_no: i18n.t('Sale No'),
      phone: i18n.t('Phone 1'),
      phone2: i18n.t('Phone 2'),
      clerk_uuid: i18n.t('Clerk'),
      description: i18n.t('Description'),
      auction_house_name: i18n.t('Auction House')
    };

const salesActivityHelper = {
  actions: {
    'create.premiums': {
      message: i18n.t('Premiums were created'),
    },
    'create.bidder': {
      message: i18n.t('Bidder was created')
    },
    'update.bidder': {
      message: i18n.t('Bidder was updated')
    },
    'delete.bidder': {
      message: i18n.t('Bidder was deleted')
    },
    'update.increment_table': {
      message: i18n.t('Increment table was updated')
    },
    'reserved': {
      message: i18n.t('Reserved')
    },
    'create.inventory': {
      message: i18n.t('Inventory was created'),
      attributeLabels: new InventoryModel().attributeLabels,
      excludeAttributes: ['primary_image', 'active_lot', 'dynamic_fields']
    },
    'update.inventory': {
      message: i18n.t('Inventory was updated'),
      attributeLabels: new InventoryModel().attributeLabels,
      excludeAttributes: ['primary_image', 'active_lot', 'dynamic_fields']
    },
    'create.auction': {
      message: i18n.t('Sale was created'),
      attributeLabels: new SaleModel().attributeLabels,
      excludeAttributes: ['premiums', 'start_date', 'end_date', 'auctioneers']
    },
    'update.auction': {
      message: i18n.t('Sale was updated'),
      attributeLabels: new SaleModel().attributeLabels,
      excludeAttributes: ['premiums', 'start_date', 'end_date', 'auctioneers']
    },
    'update.premiums': {
      message: i18n.t('Premiums were updated'),
    },
    'create.auction_note': {
      message: i18n.t('Note was created'),
      attributeLabels: new NoteForm().attributeLabels,
      excludeAttributes: ['auction']
    },
    'update.auction_note': {
      message: i18n.t('Note was updated'),
      attributeLabels: new NoteForm().attributeLabels,
      excludeAttributes: ['auction']
    },
    'delete.auction_note': {
      message: i18n.t('Note was deleted'),
    },
    'create.auction_increment': {
      message: i18n.t('Increment(s) were created'),
    },
    'update.auction_increment': {
      message: i18n.t('Increment(s) were updated'),
    },
    'create.auction_image': {
      message: i18n.t('Auction Image was created'),
      attributeLabels: {name: i18n.t('Name')},
      excludeAttributes: ['auction', 'data', 'image', 'filename', 'position', 'processing', 'generated_name']
    },
    'published': {
      message: i18n.t('Sale was published'),
    },
    'unpublished': {
      message: i18n.t('Sale was unpublished'),
    },
    'listed': {
      message: i18n.t('Sale was listed'),
    },
    'create.pre_bid': {
      message: i18n.t('Pre bid was created'),
      attributeLabels: saleActivityLabels,
      excludeAttributes: ['auction_house', 'auction']
    },
    'pending': {
      message: i18n.t('Pending'),
      attributeLabels: new BidModel().attributeLabels,
      excludeAttributes: ['auction'],
    },
    'confirmed': {
      message: i18n.t('Confirmed'),
      attributeLabels: new BidModel().attributeLabels,
      excludeAttributes: ['auction'],
    },
    'rejected': {
      message: i18n.t('Rejected'),
      attributeLabels: new BidModel().attributeLabels,
      excludeAttributes: ['auction'],
    },
    'update.pre_bid': {
      message: i18n.t('Pre bid was updated'),
      attributeLabels: saleActivityLabels,
      excludeAttributes: ['auction_house', 'auction']
    },
    'delete.pre_bid': {
      message: i18n.t('Pre bid was deleted'),
      attributeLabels: saleActivityLabels,
      excludeAttributes: ['auction_house', 'auction']
    },
    'update.invoice': {
      message: i18n.t('Invoice was updated'),
      attributeLabels: invoiceLabels,
      excludeAttributes: ['html_pdf', 'base_64_pdf'],
    },
    'update.invoice_miscellaneous_items': {
      message: i18n.t('Invoice miscellaneous item was updated'),
    },
    'create.invoice': {
      message: i18n.t('Invoice was created'),
      attributeLabels: invoiceLabels,
      excludeAttributes: ['html_pdf', 'base_64_pdf', 'bidder', 'data', 'currency', 'auction_house'],
    },
    'paid.invoice': {
      message: i18n.t('Invoice was paid'),
    },
    'create.settlement': {
      message: i18n.t('Settlement was created'),
      attributeLabels: new SettlementModel().attributeLabels,
      excludeAttributes: ['data', 'bidder'],
    },
    'update.settlement': {
      message: i18n.t('Settlement was updated'),
      attributeLabels: new SettlementModel().attributeLabels,
    },
    'cancelled.settlements': {
      message: i18n.t('Settlement was cancelled'),
    },
    'unpaid_invoice_notification': {
      message: i18n.t('Unpaid notification was sent'),
      attributeLabels: notificationLabels,
      excludeAttributes: ['action']
    },
    'overdue_invoice_notification': {
      message: i18n.t('Overdue notification was sent'),
      attributeLabels: notificationLabels,
      excludeAttributes: ['action']
    },
    'auction_ends_in_thirty_minutes_notification': {
      message: i18n.t('"Auction Ends In 30 Minutes" notification was sent')
    },
    'unmark_collected': {
      message: i18n.t('Invoice was unmarked collected'),
    },
    'mark_collected': {
      message: i18n.t('Invoice was marked collected'),
    },
    'cancelled.invoice': {
      message: i18n.t('Invoice was cancelled'),
    },
    'unpaid.invoice': {
      message: i18n.t('Invoice status changed to unpaid'),
    },
  },
  async valueVisualizer(activity, attr, val) {
    let returnValue = val;

    if (activity.name === 'auction') {
      if (activity.action === 'create.auction' || activity.action === 'update.auction') {
        if (attr === 'title' || attr === 'description') {
          returnValue = Formatter.getByMasterLanguage(val);
        } else if (attr === 'dates') {
          returnValue = '';
          val.forEach((date) => {
            let dates = [
              this.$options.filters.toDateTime(date.start_date),
              this.$options.filters.toDateTime(date.end_date)
            ];
            returnValue += dates.filter(val => val && val !== '').join(' - ') + "<br>";
          })
        } else if (attr === 'branch') {
          let branch = this.branches.rows.find(b => b.uuid === val);
          returnValue = branch ? branch.name : '';
        } else if (attr === 'department') {
          let department = this.departments.rows.find(d => d.uuid === val);
          returnValue = department ? Formatter.getByMasterLanguage(department.name) : '';
        }
      }
    } else if (activity.name === 'inventory') {
      if (attr === 'branch') {
        let branch = this.branches.rows.find(b => b.uuid === val);
        returnValue = branch ? branch.name : '';
      } else if (attr === 'customer') {
        let response = await CustomerService.getCustomer(val);
        returnValue = response.body.display_name;
      } else if (attr === 'receipt') {
        let response = await ReceiptService.getReceipt(val);
        returnValue = response.body.receipt_no;
      } else if (attr === 'category') {
        let response = await CategoryService.getCategory(val);
        returnValue = Formatter.getByMasterLanguage(response.body.name);
      } else if (attr === 'department') {
        let department = this.departments.rows.find(d => d.uuid === val);
        returnValue = department ? Formatter.getByMasterLanguage(department.name) : '';
      } else if (attr === 'status') {
        returnValue = val.charAt(0).toUpperCase() + val.slice(1);
      } else if (attr === 'item_location') {
        const location = HelperService.searchNested(store.state.global.itemLocations, val, 'uuid', 'children')
        if (location) returnValue = location.name
      }
    } else if (activity.name === 'settlement') {
      if (attr === 'no') {
        returnValue = activity.data.no;
      } else if (attr === 'data') {
        returnValue = '';
      } else if (attr === 'bidder') {
        returnValue = activity.data.bidder;
      } else if (attr === 'branch') {
        let branch = this.branches.rows.find(b => b.uuid === activity.data.branch);
        returnValue = branch ? branch.name : '';
      } else if (attr === 'status') {
        returnValue = activity.data.status ? activity.data.status : '';
      } else if (attr === 'auction') {
        returnValue = activity.data.auction ? activity.data.auction : '';
      } else if (attr === 'currency') {
        returnValue = activity.data.currency ? activity.data.currency : '';
      } else if (attr === 'customer') {
        let response = await CustomerService.getCustomer(activity.data.customer);
        returnValue = response.body.display_name;
      }
    } else if (activity.name === "pre_bid") {
      if (attr === 'bidder') {
        let response = await BiddersService.getBidder(activity.data.sale_no, activity.data.paddle_number, 'customer.billing_address');
        if (response.success) {
          returnValue = response.body.customer.display_name;
        } else {
          returnValue = 'N/A'
        }
      }
    } else if (activity.name === "invoice") {
      if (attr === 'branch') {
        let branch = this.branches.rows.find(b => b.uuid === activity.data.branch);
        returnValue = branch ? branch.name : '';
      } else if (attr === 'customer') {
        let response = await CustomerService.getCustomer(activity.data.customer);
        returnValue = response.body.display_name;
      } else if (attr === 'auction') {
        let response = await SalesService.getSale(val);
        returnValue = response.body.sale_no;
      }
    } else if (activity.name === 'mailing') {
      if (attr === 'data') {
        returnValue = {}
        returnValue[i18n.t("Invoice")] = val.invoice_no;
        returnValue[i18n.t("Customer")] = val.customer_display_name;
        returnValue[i18n.t("Customer email")] = val.to_email;
        returnValue[i18n.t("Invoice type")] = val.invoice_type;
        returnValue[i18n.t("Success")] = val.success ? i18n.t("Yes") : i18n.t("No");
        returnValue[i18n.t("Sent at")] = moment(val.sent_at).format('DD-MM-YYYY');
      }
    }
    return returnValue;
  },

}


export default salesActivityHelper;
